import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ComplexValue } from 'app/types-shared';
import { actionCreators } from './actions';
import {
  selectRequestedModel, selectListBranch, selectListBranchState,
  selectListActions, selectUpdating, selectActionErrors,
  selectSubscriberGroupValue, selectActiveValue,
  selectVisibility, selectSelectedRows, selectActiveValueError
} from './selectors';
import {
  AddListAction, CancelChanges, ClearActionErrors, ClearAll,
  UpdateSubscriberGroup, UpdateActiveValue, UpdateVisibility, SelectRow
} from './action-types';
import { UserMembersActionType } from './constants';
import { Visibility } from './types-store';
import { RowSelection } from './types';
import { AppDispatch } from 'app/infrastructure/configure-store';

export const useActionCreators = () => {

  const dispatch: AppDispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators, dispatch), [dispatch]);
};

export const useClearAllEffect = (
  shouldPrompt: boolean
) => {
  const dispatch: AppDispatch = useDispatch();

  useEffect(
    () => {
      if (shouldPrompt) {
        dispatch(new ClearAll());
      }
    }, [dispatch]);
};

export const useDetailsSelectors = () => {

  const subscriberGroupValue = useSelector(selectSubscriberGroupValue);
  const activeValue = useSelector(selectActiveValue);

  return {
    subscriberGroupValue,
    activeValue
  };
};

export const useDetailsCallbacks = () => {

  const subscriberGroupValue = useSelector(selectSubscriberGroupValue);

  const dispatch: AppDispatch = useDispatch();

  const updateSubscriberGroup = useCallback(
    (value: ComplexValue<number>) => {
      if (subscriberGroupValue?.value !== value?.value) {
        dispatch(new UpdateSubscriberGroup(value));
      }
    },
    [subscriberGroupValue?.value, dispatch]
  );

  const updateActiveValue = useCallback(
    (value: ComplexValue<number>) => {
      dispatch(new UpdateActiveValue(value));
    },
    [dispatch]
  );

  return {
    updateSubscriberGroup,
    updateActiveValue
  };
};

export const useListBranchSelectors = () => {

  const listBranch = useSelector(selectListBranch);
  const listBranchState = useSelector(selectListBranchState);

  return {
    listBranch,
    listBranchState
  };
};

export const useListBranchCallbacks = () => {

  const requestedModel = useSelector(selectRequestedModel);
  const { loadListBranch } = useActionCreators();

  const loadFromParent = useCallback(
    (id: number, startIndex: number) => {
      loadListBranch(requestedModel, id, startIndex);
    },
    [requestedModel, loadListBranch]
  );

  return {
    loadFromParent
  };
};

export const useListActionsSelectors = () => {

  const listActions = useSelector(selectListActions);
  const updating = useSelector(selectUpdating);
  const actionErrors = useSelector(selectActionErrors);
  const activeValueError = useSelector(selectActiveValueError);

  return {
    listActions,
    updating,
    actionErrors,
    activeValueError
  };
};

export const useListActionsCallbacks = () => {

  const { saveChanges: saveChangesAction } = useActionCreators();
  const requestedModel = useSelector(selectRequestedModel);
  const { activeValue } = useDetailsSelectors();
  const { listActions } = useListActionsSelectors();

  const dispatch: AppDispatch = useDispatch();

  const addListAction = useCallback(
    (path: number[], type: UserMembersActionType, propagate: boolean) => {
      dispatch(new AddListAction([{ path, type, propagate }]));
    },
    [dispatch]
  );

  const addListActions = useCallback(
    (paths: number[][], type: UserMembersActionType, propagate: boolean) => {
      dispatch(new AddListAction(paths.map(path => ({ path, type, propagate }))));
    },
    [dispatch]
  );

  const saveChanges = useCallback(
    (successMessage: string, errorMessage: string) => {
      saveChangesAction(requestedModel, activeValue, listActions, successMessage, errorMessage);
    }, [requestedModel, activeValue, listActions, saveChangesAction]
  );

  const cancelChanges = useCallback(
    () => {
      dispatch(new CancelChanges());
    },
    [dispatch]
  );

  const clearActionErrors = useCallback(
    (fixListActions: boolean) => {
      dispatch(new ClearActionErrors(fixListActions));
    },
    [dispatch]
  );

  return {
    addListAction,
    addListActions,
    saveChanges,
    cancelChanges,
    clearActionErrors
  };
};

export const useListSelectors = () => {

  const visiblity = useSelector(selectVisibility);
  const selectedRows = useSelector(selectSelectedRows);
  const subscriberGroupValue = useSelector(selectSubscriberGroupValue);
  const activeValue = useSelector(selectActiveValue);
  const requestedModel = useSelector(selectRequestedModel);

  return {
    requestedModel,
    visiblity,
    selectedRows,
    subscriberGroupValue,
    activeValue
  };
};

export const useListCallbacks = () => {

  const dispatch: AppDispatch = useDispatch();

  const updateVisibility = useCallback(
    (visibility: Visibility) => {
      dispatch(new UpdateVisibility(visibility));
    },
    [dispatch]
  );

  const selectRow = useCallback(
    (selection: RowSelection) => {
      dispatch(new SelectRow(selection));
    },
    [dispatch]
  );

  return {
    updateVisibility,
    selectRow
  };
};

export const usePromptOnChange = <T>(
  shouldPrompt: boolean,
  onChange: (value: T) => void
) => {
  const [displayPrompt, setDisplayPrompt] = useState(false);
  const [continueWithValue, setContinueWithValue] = useState<T>();

  const onPromptedChange = (value: T) => {
    if (shouldPrompt) {
      setDisplayPrompt(true);
      setContinueWithValue(value);
    } else {
      onChange(value);
    }
  };

  const onContinue = () => {
    onChange(continueWithValue);
  };

  const onHide = () => {
    setDisplayPrompt(false);
  };

  return { displayPrompt, onPromptedChange, onContinue, onHide };
};
